import { useNavigate } from "react-router-dom";
import { useToast } from "../../components/toast";
import { useState, useEffect } from "react";
import { Competition } from "../../types/competition.types";
import { CompetitionsAPI } from "../../APIs/competitions.api";
import { EditableField } from "../../components/editable-field";
import { DateField } from "../../components/date-field";
import { useCompetitions } from "../../context/CompetitionsContext";
import { Button } from "../../components/button";
import { toUTCDateString } from "../../utils/date.utils";

interface CompetitionsEditFormProps {
  initialData: Competition;
}

export function CompetitionsEditForm({
  initialData,
}: CompetitionsEditFormProps) {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [error, setError] = useState<string | null>(null);
  const { getAdminCompetitions } = useCompetitions();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [competition, setCompetition] =
    useState<Partial<Competition>>(initialData);

  useEffect(() => {
    setCompetition(initialData);
  }, [initialData]);

  const handleError = (message: string, timeOut?: number) => {
    setError(message);
    setTimeout(() => setError(null), timeOut || 5000);
  };

  const validateDates = (): string | null => {
    if (competition.registration_end! > competition.start_date!) {
      return "La finalización del registro debe ser antes del inicio de la competencia.";
    }
    if (competition.registration_start! > competition.registration_end!) {
      return "El inicio del registro debe ser antes de su finalización.";
    }
    if (competition.start_date! > competition.end_date!) {
      return "El inicio de la competencia debe ser antes del final.";
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);

    const validationError = validateDates();
    if (validationError) {
      handleError(validationError);
      setIsSubmitting(false);
      return;
    }

    try {
      const changes = checkForChanges();
      if (!changes) {
        addToast({
          type: "info",
          message: "No se realizaron cambios en la competencia",
          location: "bottom-center",
        });
        navigate("/dashboard/competitions");
        return;
      }

      const response = await CompetitionsAPI.updateCompetition(
        initialData.id,
        changes
      );

      if (response.statusCode === 200) {
        addToast({
          type: "success",
          message: "Competencia actualizada correctamente",
          location: "bottom-center",
        });
        getAdminCompetitions();
        navigate("/dashboard/competitions");
      } else {
        handleError(`Error al actualizar la competencia: ${response.message}`);
      }
    } catch (error) {
      handleError("Error al actualizar la competencia");
    } finally {
      setIsSubmitting(false);
    }
  };

  const checkForChanges = () => {
    const changes: Record<string, any> = {};
    (Object.keys(competition) as (keyof Competition)[]).forEach((key) => {
      if (competition[key] !== initialData[key]) {
        changes[key] = key.endsWith("_date")
          ? toUTCDateString(competition[key] as string)
          : competition[key];
      }
    });
    return Object.keys(changes).length > 0 ? changes : null;
  };

  const handleChange = (e: React.ChangeEvent<HTMLElement>) => {
    const { name, value } = e.target as HTMLInputElement;
    setCompetition((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <div className="p-4 border border-gray-300 rounded-md">
      <h3 className="text-xl font-bold text-gray-800 mb-4">
        Editar competencia
      </h3>
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
          {error}
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-4">
        <EditableField
          label="Nombre"
          placeholder="Ingrese el nombre de la competencia"
          value={competition.name || ""}
          onChange={handleChange}
          type="text"
          name="name"
          minLength={3}
          maxLength={100}
        />
        <EditableField
          label="Edición"
          value={competition.edition || 0}
          placeholder="Ingrese la edición de la competencia"
          onChange={handleChange}
          type="number"
          min={1}
          name="edition"
        />
        <EditableField
          label="Descripción"
          placeholder="Ingrese la descripción de la competencia"
          value={competition.description || ""}
          onChange={handleChange}
          type="textarea"
          minLength={3}
          maxLength={1000}
          name="description"
        />
        <DateField
          label="Fecha de Inicio de Inscripciones"
          value={competition.registration_start || ""}
          onChange={handleChange}
          name="registration_start"
          type="datetime-local"
        />
        <DateField
          label="Fecha de Finalización de Inscripciones"
          value={competition.registration_end || ""}
          onChange={handleChange}
          name="registration_end"
          type="datetime-local"
        />
        <DateField
          label="Fecha de Inicio de la Competencia"
          value={competition.start_date || ""}
          onChange={handleChange}
          name="start_date"
          type="datetime-local"
        />
        <DateField
          label="Fecha de Finalización de la Competencia"
          value={competition.end_date || ""}
          onChange={handleChange}
          name="end_date"
          type="datetime-local"
        />

        <div className="flex justify-end space-x-2 mt-4">
          <Button
            type="button"
            variant="cancel"
            onClick={() =>
              navigate("/dashboard/competitions", { replace: true })
            }
            text="Cancelar"
          />
          <Button
            type="submit"
            text="Actualizar"
            variant="edit"
            disabled={isSubmitting}
          />
        </div>
      </form>
    </div>
  );
}
